// RoomClient — client of the signaling protocol (src/worker/room.ts).
//
// Events (CustomEvent.detail): 'welcome' {you, peers, reconnect}, 'join' {peer}, 'leave' {peer},
// 'signal' {from, data}, 'error' {error}, 'closed' {code}. Reconnects with backoff unless
// close() was called. Signals sent while the socket is down (a phone switching networks) are
// queued and flushed on the next welcome, so an ICE restart offer is not lost.

const MAX_QUEUE = 200;
// Mobile networks drop idle TCP connections (observed: every ~40 s on 4G). The ping is answered
// by the runtime auto-response, so it never wakes the Durable Object.
const KEEPALIVE_MS = 15_000;

export type Welcome = { t: 'welcome'; you: string; peers: string[]; reconnect: boolean };

export class RoomClient extends EventTarget {
  readonly #url: string;
  #ws: WebSocket | null = null;
  #closed = false;
  #retry = 0;
  #welcomes = 0;
  #queue: string[] = [];
  #keepalive: ReturnType<typeof setInterval>;

  constructor(url: string) {
    super();
    this.#url = url;
    this.#keepalive = setInterval(() => this.open && this.#ws!.send('{"t":"ping"}'), KEEPALIVE_MS);
  }

  /**
   * Opens the socket (reconnecting with backoff for as long as the client lives) and resolves on
   * the first welcome — whichever socket brings it. It never rejects: a server that restarts or
   * closes before the welcome (a deploy, a network change) used to leave callers waiting forever.
   */
  connect(): Promise<Welcome> {
    if (!this.#ws) this.#open();
    return this.#ready;
  }

  // Declared before #ready: class fields initialise in order, a later declaration would reset it.
  #resolveReady!: (w: Welcome) => void;
  readonly #ready = new Promise<Welcome>((resolve) => (this.#resolveReady = resolve));

  #open(): void {
    const ws = new WebSocket(this.#url);
    this.#ws = ws;
    ws.onmessage = (ev) => {
      let msg: { t?: string } & Record<string, unknown>;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      if (msg.t === 'pong' || !msg.t) return;
      if (msg.t === 'welcome') {
        this.#retry = 0;
        msg.reconnect = this.#welcomes++ > 0;
        for (const frame of this.#queue.splice(0)) ws.send(frame);
        this.#resolveReady(msg as unknown as Welcome);
      }
      this.dispatchEvent(new CustomEvent(msg.t, { detail: msg }));
    };
    ws.onclose = (ev) => {
      if (ws !== this.#ws) return;
      this.dispatchEvent(new CustomEvent('closed', { detail: { code: ev.code } }));
      if (this.#closed || ev.code === 4000) return;
      const delay = Math.min(10_000, 500 * 2 ** this.#retry++);
      setTimeout(() => !this.#closed && this.#open(), delay);
    };
  }

  get open(): boolean {
    return this.#ws?.readyState === WebSocket.OPEN;
  }

  send(to: string, data: unknown): void {
    const frame = JSON.stringify({ t: 'signal', to, data });
    if (this.open) this.#ws!.send(frame);
    else if (this.#queue.length < MAX_QUEUE) this.#queue.push(frame);
  }

  /** Test hook: drops the socket as a network change would (it then reconnects). */
  dropForTest(): void {
    this.#ws?.close(4001, 'test');
  }

  close(): void {
    this.#closed = true;
    clearInterval(this.#keepalive);
    this.#ws?.close(1000);
  }
}
